import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Button as MuiButton,
  TextField,
  Paper,
} from '@mui/material';
import { Home, FileDownload, Add } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSession } from '@hooks/useSession';
import { useOpportunity } from '@hooks/useOpportunity';
import { useStats } from '@hooks/useStats';
import { useExport } from '@hooks/useExport';
import { SessionSummary } from '@components/session/SessionSummary';
import { OpportunityList } from '@components/observation/OpportunityList';
import { Loading } from '@components/common/Loading';
import { Snackbar } from '@components/common/Snackbar';
import { useSessionNavigationStore } from '@store/session-navigation-store';
import { logger } from '@utils/logger';

export function SessionReviewPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { session, loading: sessionLoading, endSession } = useSession(sessionId);
  const { opportunities, loading: oppLoading } = useOpportunity(sessionId);
  const stats = useStats(opportunities);
  const { exportCSV, loading: exportLoading } = useExport();
  const { setActiveSessionId } = useSessionNavigationStore();

  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const isActiveSession = session?.status === 'in_progress';

  // Keep navigation store in sync when viewing summary of active session
  useEffect(() => {
    if (sessionId && isActiveSession) {
      setActiveSessionId(sessionId);
    }
  }, [sessionId, isActiveSession, setActiveSessionId]);

  const handleSaveNotes = async () => {
    if (!sessionId) return;

    try {
      await endSession(sessionId, notes);
      setSuccess(t('sessionReview.notesSaved'));
    } catch (err) {
      logger.error('Failed to save notes:', err);
      setError(t('sessionReview.failedSaveNotes'));
    }
  };

  const handleExportCSV = async () => {
    if (!sessionId) return;

    try {
      await exportCSV([sessionId]);
      setSuccess(t('sessionReview.csvDownloaded'));
    } catch (err) {
      logger.error('CSV export error:', err);
      setError(t('sessionReview.failedExport'));
    }
  };

  if (sessionLoading || oppLoading) {
    return <Loading message={t('observation.loadingSession')} />;
  }

  if (!session) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">
            {t('observation.sessionNotFound')}
          </Typography>
          <MuiButton onClick={() => navigate('/')} sx={{ mt: 2 }}>
            {t('common.goHome')}
          </MuiButton>
        </Box>
      </Container>
    );
  }

  // Active session summary — full statistics with action buttons
  if (isActiveSession) {
    return (
      <Container maxWidth="md" sx={{ py: 3 }}>
        <SessionSummary session={session} statistics={stats} />

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('sessionReview.recordedOpportunities')}
          </Typography>
          <OpportunityList opportunities={opportunities} />
        </Box>

        <MuiButton
          variant="contained"
          size="large"
          fullWidth
          startIcon={<Add />}
          onClick={() => navigate(`/session/${sessionId}/observe`)}
          sx={{ mt: 2 }}
        >
          {t('sessionReview.continueRecording')}
        </MuiButton>

        <Snackbar
          open={!!error}
          message={error || ''}
          severity="error"
          onClose={() => setError(null)}
        />
        <Snackbar
          open={!!success}
          message={success || ''}
          severity="success"
          onClose={() => setSuccess(null)}
        />
      </Container>
    );
  }

  // Completed session review — full view with notes, export, summary
  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <SessionSummary session={session} statistics={stats} />

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('sessionReview.sessionNotes')}
        </Typography>
        <TextField
          multiline
          rows={4}
          fullWidth
          placeholder={t('sessionReview.notesPlaceholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mb: 2 }}
        />
        <MuiButton
          variant="outlined"
          onClick={handleSaveNotes}
          disabled={!notes}
        >
          {t('sessionReview.saveNotes')}
        </MuiButton>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('sessionReview.exportData')}
        </Typography>
        <MuiButton
          variant="contained"
          startIcon={<FileDownload />}
          onClick={handleExportCSV}
          disabled={exportLoading}
        >
          {t('sessionReview.downloadCSV')}
        </MuiButton>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {t('sessionReview.allOpportunities')}
        </Typography>
        <OpportunityList opportunities={opportunities} />
      </Box>

      <MuiButton
        variant="contained"
        size="large"
        fullWidth
        startIcon={<Home />}
        onClick={() => navigate('/')}
        sx={{ mt: 3 }}
      >
        {t('sessionReview.returnHome')}
      </MuiButton>

      <Snackbar
        open={!!error}
        message={error || ''}
        severity="error"
        onClose={() => setError(null)}
      />
      <Snackbar
        open={!!success}
        message={success || ''}
        severity="success"
        onClose={() => setSuccess(null)}
      />
    </Container>
  );
}
